import { cx } from "@jugl-web/utils";
import { useId, useMemo, ReactElement } from "react";
import { Menu, MenuItem } from "../../Menu";
import { FormGroup, FormGroupProps } from "../FormGroup";
import { SelectTrigger } from "../SelectTrigger";

export interface SelectItem {
  name: string;
  value: string;
  icon?: ReactElement;
  labelClassName?: string;
  triggerClassName?: string;
}

export type SelectProps = FormGroupProps & {
  items: SelectItem[];
  value?: string;
  placeholder?: string;
  onSelect: (value: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
};

export const Select = ({
  items,
  value,
  placeholder,
  onSelect,
  onOpen,
  onClose,
  ...formGroupProps
}: SelectProps) => {
  const id = useId();

  const menuSections = useMemo(() => {
    if (!items) return [];
    return [
      items.map<MenuItem>((item) => ({
        id: item.value,
        label: item.name,
        icon: item.icon,
        isSelected: item.value === value,
        labelClassName: cx(
          "text-base text-dark px-2 py-2.5 truncate",
          item.labelClassName
        ),
        triggerClassName: item.triggerClassName,
        onSelect: () => onSelect(item.value),
      })),
    ];
  }, [items, value, onSelect]);

  const selectedValue = useMemo(
    () => items.find((item) => item.value === value)?.name,
    [items, value]
  );

  return (
    <FormGroup labelFor={id} {...formGroupProps}>
      <Menu
        placement="bottom-end"
        adjustToTriggerWidth
        sections={menuSections}
        autoClose
        renderTrigger={({ Trigger, triggerRef, isOpen }) => {
          const pageElement = document.querySelectorAll(
            '[data-component="page"]'
          );
          if (isOpen) {
            pageElement?.forEach((el) => {
              el.classList.add("overflow-y-hidden");
            });
            onOpen?.();
          } else {
            pageElement?.forEach((el) => {
              el.classList.remove("overflow-y-hidden");
            });
            onClose?.();
          }
          return (
            <Trigger
              ref={triggerRef}
              as={SelectTrigger}
              label={selectedValue || placeholder}
              isOpen={isOpen}
              hasValue={!!value}
              isDisabled={formGroupProps.isDisabled}
              isInvalid={formGroupProps.isInvalid}
              className={formGroupProps.classNames?.wrapperClassName}
            />
          );
        }}
      />
    </FormGroup>
  );
};
